import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Feature } from '@ats/api/cm';
import { CmInterceptHeaders } from '@ats/cm/core';
import { filterNullOrUndefined } from '@ats/utility';
import { Apollo, gql } from 'apollo-angular';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import {
	catchError,
	distinctUntilChanged,
	filter,
	map,
	skipUntil,
	switchMap,
} from 'rxjs/operators';
import { ActiveClientFeaturesQuery, ClientFeatureResponse } from './ip-client-queries';
const CLIENTS = gql`
	query clients {
		tools {
			defaultClientAliasList
		}
	}
`;

@Injectable({
	providedIn: 'root',
})
export class IpClientService {
	clients$: Observable<string[]> = this.fetchClients$();
	readonly active$: Observable<string | null>;
	readonly activeChange$: Observable<string | null>;
	private clientListDisabledSubj$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	get isClientListDisabled$(): Observable<boolean> {
		return this.clientListDisabledSubj$.asObservable();
	}

	constructor(
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly apollo: Apollo
	) {
		this.active$ = combineLatest([
			this.clients$,
			this.route.queryParamMap.pipe(map(params => params.get('client'))),
		]).pipe(
			map(([clients, id]) => {
				if (!id) {
					return null;
				}
				const active = clients.find(client => client === id);
				return active || null;
			})
		);

		const activeWithValue$ = this.active$.pipe(filter(client => !!client));

		this.activeChange$ = this.active$.pipe(skipUntil(activeWithValue$), distinctUntilChanged());
	}

	setClientListDisabledStatus(disabled: boolean): void {
		this.clientListDisabledSubj$.next(disabled);
	}

	selectClient(id: string | null) {
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { client: id },
			queryParamsHandling: 'merge',
		});
	}

	getActiveFeatures$(): Observable<Pick<Feature, 'id' | 'enabled' | 'key'>[]> {
		return this.active$.pipe(
			filterNullOrUndefined(),
			switchMap(client =>
				this.apollo
					.watchQuery<{ clientSponsor: ClientFeatureResponse }>({
						query: ActiveClientFeaturesQuery,
						context: {
							headers: CmInterceptHeaders.ClientHeader(client),
						},
						fetchPolicy: 'network-only',
					})
					.valueChanges.pipe(
						map(({ data }) =>
							(((data || {}).clientSponsor || {}).features || []).filter(
								feat => feat.enabled
							)
						)
					)
			)
		);
	}

	private fetchClients$(): Observable<string[]> {
		return this.apollo
			.watchQuery<{ tools: { defaultClientAliasList: string[] } }>({
				query: CLIENTS,
			})
			.valueChanges.pipe(
				map(({ data }) => ((data || {}).tools || {}).defaultClientAliasList || []),
				catchError(() => of([]))
			);
	}
}
